import { returnNonNull } from 'src/common/helpers/ArraysHelper';
import {
	buildBoundingBox,
	buildElementInfo,
	buildTappableAreaMetadata,
	buildTaskManagerTickets,
	MOBILE_SEVERITY_MAP
} from 'src/common/helpers/MobileQueriesHelper';
import {
	MobileA11yGuideline,
	MobileCommentData,
	MobileIssueDetails
} from 'src/common/types/MobileSingleSessionTypes';

import { MfaIssueDetailsQuery } from '../MfaIssueDetails.generated';

const transformIssueToMobileIssueDetails = (
	issue: NonNullable<NonNullable<MfaIssueDetailsQuery['issues']>[0]>,
	userName: string,
	scanDate: string
): MobileIssueDetails => {
	const elementInfo = buildElementInfo(issue);
	const taskManagerTickets = buildTaskManagerTickets(issue);
	const boundingBox = buildBoundingBox(issue);

	// Parse a11y guidelines from tags if available
	const a11yGuidelines: MobileA11yGuideline[] = [];
	issue.tags__description?.forEach((item, index) => {
		const title = item.trim();
		const link = issue.tags__link?.[index].trim();

		if (title) {
			a11yGuidelines.push({ title, link: link ?? '' });
		}
	});

	// prepare comment data
	const comment: MobileCommentData | undefined = issue.note
		? {
				text: issue.note,
				lastModified: issue.note_creation_time,
				user: userName
			}
		: undefined;

	return {
		type: issue.type_name,
		description: issue.description,
		comment,
		severity: MOBILE_SEVERITY_MAP[issue.severity_name],
		signature: issue.signature,
		scanName: issue.scan_name,
		elementIndex: issue.elements__index?.[0] ?? '',
		scanDate,
		screenshotDetails: {
			pageScreenshotUrl: issue.screenshot,
			boundingBox,
			viewportWidth: issue.viewport_width,
			viewportHeight: issue.viewport_height
		},
		taskManagerTickets,
		elementInfo,
		knowledgeBaseLink: issue.knowledge_base_link,
		expectations: [], // replace when schema will support issue expectations
		a11yGuidelines,
		tappableAreaMetadata: buildTappableAreaMetadata(issue.metadata)
	};
};

export const transformMfaIssueDetailsResults = (
	result: MfaIssueDetailsQuery
): MobileIssueDetails | null => {
	const issues = result.issues?.filter(returnNonNull);

	if (!issues?.length) {
		return null;
	}

	const sessionRuns = result.mfa_session_runs?.filter(returnNonNull);
	const userName = sessionRuns?.[0]?.user_name ?? '';
	const scanDate = sessionRuns?.[0]?.run_time ?? '';

	return transformIssueToMobileIssueDetails(issues[0], userName, scanDate);
};
